import { ExternalLink, Globe2, X, ZoomIn, ZoomOut } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import {
  isBrowserSessionViewConfig,
  viewRefKey,
  type BrowserSessionViewConfig,
  type ViewRef,
} from "@/api/views";
import { Button } from "@/components/ui/button";
import {
  DEFAULT_SESSION_VIEWPORT_HEIGHT,
  DEFAULT_SESSION_VIEWPORT_WIDTH,
  useBrowserSessionZoom,
} from "@/components/views/browser-session-zoom";
import type { AccessTokenGetter } from "@/lib/utils";
import { cn } from "@/lib/utils";

export interface BrowserSessionViewProps {
  viewRef: ViewRef;
  /** Retained for the shared view-renderer contract; the live URL needs no API call. */
  getToken: AccessTokenGetter;
  title: string;
  config?: BrowserSessionViewConfig;
  onClose?: () => void;
  className?: string;
}

function liveUrl(config: BrowserSessionViewConfig | undefined): string | null {
  if (!isBrowserSessionViewConfig(config)) return null;
  if (
    config.live_url !== config.live_url.trim()
    || /\s/.test(config.live_url)
    || config.live_url.includes("\\")
  ) {
    return null;
  }
  try {
    const parsed = new URL(config.live_url);
    return parsed.protocol === "https:" && !parsed.username && !parsed.password
      ? config.live_url
      : null;
  } catch {
    return null;
  }
}

/** Live, read/write Browserbase handoff embedded in a conversation view. */
export const BrowserSessionView = memo(function BrowserSessionView({
  viewRef,
  getToken: _getToken,
  title,
  config,
  onClose,
  className,
}: BrowserSessionViewProps) {
  const { t } = useTranslation();
  const browserConfig = isBrowserSessionViewConfig(config) ? config : undefined;
  const url = liveUrl(browserConfig);
  const displayTitle = browserConfig?.title ?? title;
  const zoom = useBrowserSessionZoom();

  return (
    <section
      className={cn("flex h-full min-h-0 min-w-0 flex-col bg-muted/20", className)}
      data-testid="browser-session-view"
    >
      <header className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b bg-background px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Globe2 className="size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold" title={displayTitle}>
              {displayTitle}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {t("viewer.browserSession.readWrite")}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {url && (
            <div className="flex items-center gap-1" role="toolbar" aria-label={t("viewer.browserSession.zoomControls")}>
              <Button type="button" variant="ghost" size="icon-sm" onClick={zoom.zoomOut}
                disabled={!zoom.canZoomOut} aria-label={t("viewer.browserSession.zoomOut")}
                title={t("viewer.browserSession.zoomOut")} data-testid="browser-session-zoom-out">
                <ZoomOut className="size-4" />
              </Button>
              <Button type="button" variant="ghost" size="sm"
                className="h-8 min-w-[3.5rem] px-2 text-xs tabular-nums"
                onClick={zoom.resetZoom} disabled={zoom.fitToWidth}
                aria-label={t("viewer.browserSession.resetZoom")}
                title={t("viewer.browserSession.fitToWidth")}
                data-testid="browser-session-reset-zoom">
                {t("viewer.browserSession.zoomLevel", { zoom: Math.round(zoom.scale * 100) })}
              </Button>
              <Button type="button" variant="ghost" size="icon-sm" onClick={zoom.zoomIn}
                disabled={!zoom.canZoomIn} aria-label={t("viewer.browserSession.zoomIn")}
                title={t("viewer.browserSession.zoomIn")} data-testid="browser-session-zoom-in">
                <ZoomIn className="size-4" />
              </Button>
            </div>
          )}
          {url && (
            <Button
              asChild
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
            >
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("viewer.browserSession.open")}
                data-testid="browser-session-open-link"
              >
                {t("viewer.browserSession.open")}
                <ExternalLink className="size-3.5" />
              </a>
            </Button>
          )}
          {onClose && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label={t("viewer.closeViewer")}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </header>

      {url ? (
        <div ref={zoom.containerRef} className="min-h-0 min-w-0 flex-1 overflow-auto" data-testid="browser-session-scroll">
          <div className="relative" data-testid="browser-session-stage"
            style={{ width: DEFAULT_SESSION_VIEWPORT_WIDTH * zoom.scale, height: DEFAULT_SESSION_VIEWPORT_HEIGHT * zoom.scale }}>
            <iframe
              key={`${viewRefKey(viewRef)}:${url}`}
              src={url}
              title={displayTitle}
              sandbox="allow-same-origin allow-scripts"
              allow="clipboard-read; clipboard-write"
              referrerPolicy="no-referrer"
              className="absolute left-0 top-0 border-0"
              style={{ width: DEFAULT_SESSION_VIEWPORT_WIDTH, height: DEFAULT_SESSION_VIEWPORT_HEIGHT,
                transform: `scale(${zoom.scale})`, transformOrigin: "top left" }}
              data-testid="browser-session-iframe"
            />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
          <Globe2 className="size-5" />
          <p>{t("viewer.browserSession.unavailable")}</p>
        </div>
      )}
    </section>
  );
});
